import { invokeWithSessionRetry } from "@/lib/invoke-with-session-retry";
import { buildAiIdempotencyKey, deductBudget, ensureTokenBudget, toFunctionErrorMessage, type AiBudgetOptions, type AiUsageSnapshot } from "@/lib/ai-client-common";
import type { ExplainLanguage } from "@/lib/ai-explain-client";
import type { ToofiRuntimeTelemetry } from "@/lib/toofi/telemetry-contract";
import type {
  ReaderAiContextKind,
  ReaderAiDocumentRef,
  GuidedCheckKind,
  GuidedStepBlock,
  GuidedStepFeedbackBlock,
  ReaderAiVisualRef,
  ReaderAiLearningMode,
  ReaderAiMemoryUsage,
  ReaderReferenceMode,
  ReaderAiScope,
  ReaderAiSourceOrigin,
  ReaderAiSuggestion,
  ToofiMemoryMode,
  ToofiModeSelectionSource,
  ToofiProductIntent,
  ToofiProductMode,
} from "@/types/agentic-chat";
import type { StudentToofiSurface, StudentToofiSurfaceContext } from "@/types/student-toofi";

export type ReaderAiChatRequest = {
  prompt: string;
  threadId?: string | null;
  resourceId?: string;
  linkedResourceId?: string | null;
  linkedPlanId?: string | null;
  surface?: StudentToofiSurface;
  surfaceContext?: StudentToofiSurfaceContext;
  workspaceScope?: "student_workspace" | "resource";
  language: ExplainLanguage;
  mode: "context_chat" | "general_chat";
  productMode?: ToofiProductMode | null;
  productIntent?: ToofiProductIntent | null;
  memoryMode?: ToofiMemoryMode | null;
  modeSelectionSource?: ToofiModeSelectionSource | null;
  entryPointId?: string | null;
  contextText?: string;
  contextKind?: ReaderAiContextKind;
  contextScope?: ReaderAiScope;
  referenceMode?: ReaderReferenceMode;
  useFileContext?: boolean;
  documentReference?: ReaderAiDocumentRef | null;
  visualContext?: ReaderAiVisualRef | null;
  pageNumber?: number;
  recentTurns?: Array<{ role: "user" | "assistant"; text: string }>;
  threadSummary?: string;
  activeGoals?: string[];
  openLoops?: string[];
  documentSummary?: string;
  memorySnapshot?: {
    profile?: {
      language?: "ar" | "en" | "mixed";
      verbosity?: "short" | "balanced" | "detailed";
      tonePreference?: "calm" | "supportive" | "neutral";
      explanationStyle?: ReaderAiLearningMode;
    } | null;
    courseWeakConcepts?: string[];
    sessionActiveMode?: string | null;
  };
  guidedLessonAction?: {
    type: "answer" | "continue" | "skip";
    lessonId: string;
    stepIndex: number;
    answer?: string;
    checkKind?: GuidedCheckKind;
  } | null;
  idempotencyKey?: string;
};

export type ReaderAiChatResponse = {
  blocks: Array<
    | { type: "markdown"; markdown: string }
    | { type: "explanation"; text: string; level?: "quick" | "detailed" | "eli5" | "step_by_step"; language?: "ar" | "en" | "mixed" }
    | { type: "summary"; items: string[] }
    | { type: "plan_overview"; id?: string; title: string; goalSummary: string; horizonLabel: string; targetDate?: string | null; estimatedTotalMinutes?: number | null; status?: "draft" | "active" | "completed" | "archived"; confirmationRequired?: boolean; savedPlanId?: string | null }
    | { type: "plan_milestones"; planId?: string | null; items: Array<{ id: string; title: string; targetDate?: string | null; status?: "pending" | "active" | "completed" | "skipped"; summary?: string }> }
    | { type: "daily_plan"; planId?: string | null; date: string; totalMinutes?: number | null; items: Array<{ id: string; text: string; estimatedMinutes?: number | null; resourceId?: string | null; resourceTitle?: string | null; checked?: boolean }> }
    | { type: "plan_checkin"; title?: string; summary: string; status?: "on_track" | "behind" | "at_risk" | "completed"; nextAction?: string }
    | { type: "table"; columns: string[]; rows: string[][] }
    | { type: "math"; latex: string; display?: boolean; explanation?: string }
    | { type: "formula_sheet"; title?: string; items: Array<{ label: string; latex?: string; text?: string }> }
    | { type: "comparison"; title?: string; leftLabel: string; rightLabel: string; rows: Array<{ topic: string; left: string; right: string }> }
    | { type: "checklist"; title?: string; items: Array<{ id: string; text: string; checked?: boolean }> }
    | { type: "reflection"; prompt: string; suggestedResponses?: string[] }
    | { type: "worked_example"; title?: string; steps: string[]; takeaway?: string }
    | {
        type: "mcq";
        id?: string;
        prompt?: string;
        options?: Array<{ id?: string; label: string }>;
        correctIndex?: number | null;
        attemptId?: string | null;
        multi?: boolean;
        questions?: Array<{
          id?: string;
          prompt: string;
          options: Array<{ id?: string; label: string }>;
          correctIndex?: number | null;
          attemptId?: string | null;
          multi?: boolean;
        }>;
      }
    | {
        type: "essay_prompt";
        id?: string;
        prompt?: string;
        minWords?: number;
        prompts?: Array<{
          id?: string;
          prompt: string;
          minWords?: number;
        }>;
      }
    | { type: "note"; id?: string; title: string; content: string; saved?: boolean }
    | GuidedStepBlock
    | GuidedStepFeedbackBlock
  >;
  sourceOrigin: ReaderAiSourceOrigin;
  memoryUsage: ReaderAiMemoryUsage;
  learningMode: ReaderAiLearningMode;
  suggestions: ReaderAiSuggestion[];
  nextThreadSummary?: {
    rollingSummary: string;
    activeGoals: string[];
    openLoops: string[];
  };
  telemetry?: ToofiRuntimeTelemetry;
  runtime?: {
    premiumReasoningAttempted?: boolean;
    premiumReasoningUsed?: boolean;
    premiumReasoningFallbackReason?: string | null;
    deepReviewGeneration?: boolean;
    planCheckinIntent?: boolean;
    examModeEnabled?: boolean;
    masteryInsightsEnabled?: boolean;
    longContextAnalysisEnabled?: boolean;
    outputGovernor?: {
      retryUsed?: boolean;
      normalized?: boolean;
      fallbackUsed?: boolean;
      parseSource?: string | null;
      qualityReasons?: string[];
      qualitySeverity?: string | null;
    };
  };
  usage?: AiUsageSnapshot;
  productMode?: ToofiProductMode | null;
  productIntent?: ToofiProductIntent | null;
};

export async function requestReaderAiChat(
  req: ReaderAiChatRequest,
  options?: AiBudgetOptions,
): Promise<ReaderAiChatResponse> {
  ensureTokenBudget(options, 1200);

  const { data, error } = await invokeWithSessionRetry<ReaderAiChatResponse>("reader-ai-chat", {
    body: {
      ...req,
      idempotencyKey:
        req.idempotencyKey ??
        buildAiIdempotencyKey({
          feature: "reader-ai-chat",
          prompt: req.prompt.trim(),
          threadId: req.threadId ?? "",
          resourceId: req.resourceId ?? "",
          linkedResourceId: req.linkedResourceId ?? "",
          linkedPlanId: req.linkedPlanId ?? "",
          surface: req.surface ?? "unknown",
          mode: req.mode,
          productMode: req.productMode ?? "",
          productIntent: req.productIntent ?? "",
          memoryMode: req.memoryMode ?? "",
          modeSelectionSource: req.modeSelectionSource ?? "",
          entryPointId: req.entryPointId ?? "",
          contextKind: req.contextKind ?? "general",
          referenceMode: req.referenceMode ?? "none",
          contextScope: req.contextScope ?? "general_scope",
          pageNumber: req.pageNumber ?? null,
          recentTurns: (req.recentTurns ?? []).slice(-6),
        }),
    },
  });
  if (error || !data) {
    throw new Error(await toFunctionErrorMessage(error, "Failed to run study chat"));
  }

  if (options?.tokenBudget && typeof options.isExamSeason === "boolean") {
    deductBudget({ ...options, estimatedCost: options.estimatedCost ?? data.telemetry?.tokenCost ?? 1200 }, 1200);
  }

  return data as ReaderAiChatResponse;
}
